import { ComponentProps } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Button } from '../button/Button';
import { ChevronLeft } from '../icons/ChevronLeft';
import { ChevronRight } from '../icons/ChevronRight';

const Pagination = ({ className, ...props }: ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    data-slot="pagination"
    className={cn(className)}
    {...props}
  />
);

const PaginationContent = ({
  className,
  ...props
}: React.ComponentProps<'ul'>) => (
  <ul
    data-slot="pagination-content"
    className={cn('tw-gap-0.5 tw-flex tw-items-center', className)}
    {...props}
  />
);

const PaginationItem = ({ ...props }: React.ComponentProps<'li'>) => (
  <li data-slot="pagination-item" {...props} />
);

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>;

const PaginationLink = ({
  className,
  isActive,
  disabled,
  size = 'default',
  children,
  ...props
}: PaginationLinkProps) => {
  const href = disabled ? '#' : (props.href ?? '#');
  const handleClick = disabled
    ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()
    : props.onClick;

  return (
    <Button
      asChild
      variant={isActive ? 'default' : 'teriary'}
      size={size}
      className={cn(
        'tw-page-link tw-cursor-pointer',
        disabled && 'disabled',
        className,
      )}
    >
      <Link
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={disabled}
        data-slot="pagination-link"
        data-active={isActive}
        href={href}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Link>
    </Button>
  );
};

const PaginationPrevious = ({
  className,
  text = 'Previous',
  disabled,
  ...props
}: ComponentProps<typeof PaginationLink> & {
  text?: string;
  disabled?: boolean;
}) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn('tw-pl-1.5! tw-rounded-l-md', className)}
    disabled={disabled}
    {...props}
  >
    <ChevronLeft svgProps={{ className: 'tw-me-2 -tw-ml-1' }} />
    <span className="tw-hidden sm:tw-block">{text}</span>
  </PaginationLink>
);

const PaginationNext = ({
  className,
  text = 'Next',
  disabled,
  ...props
}: ComponentProps<typeof PaginationLink> & {
  text?: string;
  disabled?: boolean;
}) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn('tw-pr-1.5! tw-rounded-r-md', className)}
    disabled={disabled}
    {...props}
  >
    <span className="tw-hidden sm:tw-block">{text}</span>
    <ChevronRight svgProps={{ className: 'tw-ms-2 -tw-mr-1' }} />
  </PaginationLink>
);

const PaginationEllipsis = ({
  className,
  ...props
}: ComponentProps<'span'>) => (
  <span
    aria-hidden
    data-slot="pagination-ellipsis"
    className={cn(
      "tw-size-8 tw-items-center tw-justify-center [&_svg:not([class*='size-'])]:tw-size-4 tw-flex",
      className,
    )}
    {...props}
  >
    <span className="tw-px-3 tw-py-3 -tw-ml-px tw-text-ellipsis">...</span>
  </span>
);

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
